import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import '../styles/navigation.css';

export const Navigation = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/supper-club', label: 'Supper Club', isHighlight: true },
    { path: '/services', label: 'Luxury Hospitality' },
    { path: '/booking', label: 'Reserve Experience' },
    { path: '/consultations', label: 'Virtual Consultations', isNew: true },
    { path: '/meal-plans', label: 'Meal Subscriptions', isNew: true },
    { path: '/custom-cakes', label: 'Artisan Cakes' },
    { path: '/chef-marketplace', label: 'Chef Marketplace', isNew: true },
    { path: '/event-planning', label: 'Event Planning', isNew: true },
    { path: '/art-gallery', label: 'Artemis Art Gallery', isExclusive: true },
    { path: '/giveaway', label: 'Prizes Giveaway', isExclusive: true },
    { path: '/customer-portal', label: 'Client Portal', isAccount: true },
    { path: '/chef-portal', label: 'Chef Portal', isAccount: true },
    { path: '/admin/login', label: 'Admin Dashboard', isAdmin: true },
    { path: '/corporate', label: 'Executive Events' },
    { path: '/gift-packages', label: 'Luxury Gifts' },
    { path: '/farm-to-table', label: 'Estate Dining' },
    { path: '/travel-with-chef', label: 'Private Travel' },
    { path: '/wellness-retreat', label: 'Wellness Escapes' },
    { path: '/loyalty', label: 'VIP Privileges' },
    { path: '/blog', label: 'Journal' },
    { path: '/inquiry', label: 'Bespoke Inquiry' },
    { path: '/about', label: 'About Us' }
  ];

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      {/* Main Navigation Header */}
      <nav className="navigation-header" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
        transition: 'all 0.3s ease'
      }}>
        <div className="container">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            height: '70px',
            padding: '0 20px'
          }}>
            {/* Logo */}
            <Link to="/" className="navigation-logo" style={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_chef-luxury-1/artifacts/4gytiwjf_IMG_0369.jpeg" 
                alt="Decadent Palete Logo" 
                style={{ 
                  height: '45px', 
                  width: 'auto',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 2px 8px rgba(212, 175, 55, 0.3))'
                }} 
              />
            </Link>
            
            {/* Desktop Menu - Hidden on mobile */}
            <ul className="navigation-menu" style={{
              display: 'none',
              gap: '32px',
              margin: 0,
              padding: 0,
              listStyle: 'none'
            }}>
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    style={{
                      color: link.isHighlight 
                        ? '#000000' 
                        : link.isNew
                          ? '#d4af37'
                          : link.isAccount 
                            ? (location.pathname === link.path ? '#d4af37' : '#d4af37')
                            : link.isAdmin
                              ? (location.pathname.includes('/admin') ? '#dc2626' : '#dc2626')
                              : (location.pathname === link.path ? '#d4af37' : 'rgba(255, 255, 255, 0.9)'),
                      background: link.isHighlight 
                        ? 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)'
                        : link.isNew
                          ? 'rgba(212, 175, 55, 0.15)'
                          : link.isAccount
                            ? 'rgba(212, 175, 55, 0.1)'
                            : link.isAdmin
                              ? 'rgba(220, 38, 38, 0.1)'
                              : 'none',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: link.isHighlight || link.isAccount || link.isAdmin || link.isNew ? '600' : '500',
                      letterSpacing: '0.5px',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      padding: (link.isHighlight || link.isAccount || link.isAdmin || link.isNew) ? '8px 16px' : '0',
                      borderRadius: (link.isHighlight || link.isAccount || link.isAdmin || link.isNew) ? '4px' : '0',
                      border: link.isNew
                        ? '1px solid rgba(212, 175, 55, 0.4)'
                        : link.isAccount 
                          ? '1px solid rgba(212, 175, 55, 0.3)' 
                          : link.isAdmin 
                          ? '1px solid rgba(220, 38, 38, 0.3)' 
                          : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (link.isHighlight) {
                        e.target.style.transform = 'scale(1.05)';
                      } else if (location.pathname !== link.path) {
                        e.target.style.color = '#d4af37';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (link.isHighlight) {
                        e.target.style.transform = 'scale(1)';
                      } else if (location.pathname !== link.path) {
                        e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                      }
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Hamburger Menu Button */}
            <button
              onClick={toggleMenu}
              className="hamburger-menu-button"
              style={{
                background: 'none',
                border: 'none',
                color: '#ffffff',
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '4px',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 2001,
                minWidth: '44px',
                minHeight: '44px',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation'
              }}
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          opacity: isMenuOpen ? 1 : 0,
          visibility: isMenuOpen ? 'visible' : 'hidden',
          transition: 'all 0.3s ease',
          zIndex: 1500,
          backdropFilter: 'blur(5px)',
          pointerEvents: isMenuOpen ? 'auto' : 'none'
        }}
        onClick={toggleMenu}
      />

      {/* Luxury Hamburger Menu */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: '100%',
          maxWidth: '400px',
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
          transform: isMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 2000,
          boxShadow: isMenuOpen ? '-10px 0 30px rgba(0, 0, 0, 0.5)' : 'none',
          borderLeft: '1px solid rgba(212, 175, 55, 0.2)',
          pointerEvents: isMenuOpen ? 'auto' : 'none',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* Menu Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 32px',
          borderBottom: '1px solid rgba(212, 175, 55, 0.1)'
        }}>
          {/* Logo in Menu */}
          <Link to="/" onClick={toggleMenu} style={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_chef-luxury-1/artifacts/4gytiwjf_IMG_0369.jpeg" 
              alt="Decadent Palete" 
              style={{ 
                height: '50px', 
                width: 'auto',
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 12px rgba(212, 175, 55, 0.4))'
              }} 
            />
          </Link>

          {/* Close Button */}
          <button
            onClick={toggleMenu}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffffff',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(212, 175, 55, 0.2)';
              e.target.style.color = '#d4af37';
              e.target.style.transform = 'rotate(90deg)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
              e.target.style.color = '#ffffff';
              e.target.style.transform = 'rotate(0deg)';
            }}
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        {/* Menu Items */}
        <div style={{
          padding: '40px 0',
          overflow: 'auto',
          height: 'calc(100vh - 120px)'
        }}>
          <nav>
            <ul style={{
              listStyle: 'none',
              margin: 0,
              padding: '0 32px'
            }}>
              {navLinks.map((link, index) => (
                <li key={link.path} style={{
                  opacity: isMenuOpen ? 1 : 0,
                  transform: isMenuOpen ? 'translateX(0)' : 'translateX(30px)',
                  transition: `all 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s`
                }}>
                  <Link
                    to={link.path}
                    onClick={toggleMenu}
                    style={{
                      display: 'block',
                      color: link.isHighlight 
                        ? '#000000' 
                        : link.isAccount
                          ? (location.pathname === link.path ? '#d4af37' : '#d4af37')
                          : link.isAdmin
                            ? (location.pathname.includes('/admin') ? '#dc2626' : '#dc2626')
                            : (location.pathname === link.path ? '#d4af37' : 'rgba(255, 255, 255, 0.9)'),
                      background: link.isHighlight 
                        ? 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)'
                        : link.isAccount
                          ? 'rgba(212, 175, 55, 0.1)'
                          : link.isAdmin
                            ? 'rgba(220, 38, 38, 0.1)'
                            : 'none',
                      textDecoration: 'none',
                      fontSize: '18px',
                      fontWeight: link.isHighlight || link.isAccount || link.isAdmin || location.pathname === link.path ? '600' : '400',
                      letterSpacing: '0.5px',
                      padding: (link.isHighlight || link.isAccount || link.isAdmin) ? '16px 20px' : '16px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                      borderRadius: (link.isHighlight || link.isAccount || link.isAdmin) ? '6px' : '0',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      fontFamily: "'Inter', sans-serif",
                      margin: (link.isHighlight || link.isAccount || link.isAdmin) ? '8px 0' : '0',
                      border: link.isAccount 
                        ? '1px solid rgba(212, 175, 55, 0.3)' 
                        : link.isAdmin 
                          ? '1px solid rgba(220, 38, 38, 0.3)' 
                          : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (link.isHighlight) {
                        e.target.style.transform = 'scale(1.02)';
                      } else {
                        e.target.style.color = '#d4af37';
                        e.target.style.paddingLeft = '12px';
                        e.target.style.borderBottom = '1px solid rgba(212, 175, 55, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (link.isHighlight) {
                        e.target.style.transform = 'scale(1)';
                      } else {
                        e.target.style.color = location.pathname === link.path ? '#d4af37' : 'rgba(255, 255, 255, 0.9)';
                        e.target.style.paddingLeft = link.isHighlight ? '20px' : '0px';
                        e.target.style.borderBottom = '1px solid rgba(255, 255, 255, 0.05)';
                      }
                    }}
                  >
                    {link.label}
                    {location.pathname === link.path && (
                      <span style={{
                        position: 'absolute',
                        right: '0',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '4px',
                        height: '20px',
                        background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                        borderRadius: '2px'
                      }} />
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Menu Footer */}
          <div style={{
            position: 'absolute',
            bottom: '32px',
            left: '32px',
            right: '32px',
            textAlign: 'center',
            borderTop: '1px solid rgba(212, 175, 55, 0.1)',
            paddingTop: '24px'
          }}>
            <p style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '14px',
              margin: '0',
              letterSpacing: '0.5px'
            }}>
              Luxury Hospitality, Redefined.
            </p>
          </div>
        </div>
      </div>

    </>
  );
};